import { Wrench, Target, Search, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageShell, Section } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIdeas } from '@/hooks/useIdeas';

interface InstrumentCardProps {
  title: string;
  purpose: string;
  bestUsedWhen: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

function InstrumentCard({ title, purpose, bestUsedWhen, icon: Icon, path }: InstrumentCardProps) {
  const navigate = useNavigate();
  
  return (
    <div className="flex flex-col justify-between rounded-xl border border-border bg-card p-5 h-full min-w-0">
      {/* Content Block */}
      <div className="space-y-4 min-w-0">
        {/* Header */}
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-1 min-w-0">
            <h3 className="text-base font-medium text-foreground line-clamp-2 break-words">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed break-words">{purpose}</p>
          </div>
        </div>
        
        {/* Best used when */}
        <div className="text-sm break-words">
          <span className="text-muted-foreground">Best used when </span>
          <span className="text-foreground">{bestUsedWhen}</span>
        </div>
      </div>
      
      {/* Action - pinned to bottom */}
      <Button 
        onClick={() => navigate(path)}
        variant="outline"
        className="w-full mt-5"
      >
        Open Instrument
      </Button>
    </div>
  );
}

const instruments: Omit<InstrumentCardProps, 'path'>[] = [
  {
    title: 'Competitor Analyzer',
    purpose: 'Understand who else is solving the same problem and how.',
    bestUsedWhen: 'you want to validate differentiation or find gaps in existing solutions.',
    icon: Target,
  },
  {
    title: 'Product Finder',
    purpose: 'Discover products, tools, and services relevant to your idea space.',
    bestUsedWhen: 'you need inspiration or want to study adjacent offerings.',
    icon: Search,
  },
];

const instrumentPaths: Record<string, string> = {
  'Competitor Analyzer': '/app/growth-instruments/competitor-analyzer',
  'Product Finder': '/app/growth-instruments/product-finder',
};

export default function GrowthInstruments() {
  const { ideas } = useIdeas();
  const focusedIdea = ideas.find(idea => idea.is_focus);

  return (
    <PageShell>
      {/* Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-medium text-foreground">Growth Instruments</h1>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed break-words">
          Focused instruments that help your idea grow through clarity, not noise.
        </p>
        {focusedIdea && (
          <Badge variant="secondary" className="gap-1.5 py-1 px-2.5 text-xs font-normal">
            <Sparkles className="h-3 w-3 shrink-0" />
            <span className="truncate">Using: {focusedIdea.title || 'Untitled idea'}</span>
          </Badge>
        )}
      </header>

      {/* Instruments Grid */}
      <Section>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {instruments.map((instrument) => (
            <InstrumentCard 
              key={instrument.title} 
              {...instrument} 
              path={instrumentPaths[instrument.title]}
            />
          ))}
        </div>
      </Section>
    </PageShell>
  );
}
